'use client';

import { useQuery } from '@tanstack/react-query';
import { Form, Formik } from 'formik';
import { FormikField, FormikSelectField } from 'formik-ui';
import { useRouter } from 'next/navigation';
import React, { useMemo, useState, useTransition } from 'react';
import { Button, Loader } from 'ui';
import * as yup from 'yup';
import { fetchRegions } from '~/api/address';
import SimpleModal from '~/components/NewModal/SimpleModal';
import useKnownCookie from '~/hooks/useKnownCookie';

export function AppDirItemDetailsPageCalculateShippingClient({
  children,
}: {
  children?: React.ReactNode;
}) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [isPending, startTransition] = useTransition();

  const { data: regions } = useQuery(['regions'], fetchRegions, {
    enabled: open,
  });
  const sortedRegions = useMemo(
    () => regions?.sort((a, b) => a.name.localeCompare(b.name)),
    [regions],
  );

  const [shipToCountryCookie, setShipToCountryCookie] =
    useKnownCookie('ship_to_country');
  const [shipToZipCookie, setShipToZipCookie] = useKnownCookie('ship_to_zip');

  async function submit() {
    startTransition(() => {
      router.refresh();
      setOpen(false);
    });
  }

  return (
    <>
      <button className="underline" onClick={() => setOpen(true)} type="button">
        {children || 'Calculate Shipping'}
      </button>

      <SimpleModal
        open={open}
        onClose={() => setOpen(false)}
        title="Calculate Shipping"
      >
        <Loader loading={isPending}>
          <Formik
            initialValues={{
              country: shipToCountryCookie || 'US',
              zip: shipToZipCookie,
            }}
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={yup.object().shape({
              country: yup.string().required('Required'),
              zip: yup.string().required('Required'),
            })}
            onSubmit={values => {
              setShipToCountryCookie(values.country!, { expires: 365 });
              setShipToZipCookie(values.zip!, { expires: 365 });
              submit();
            }}
          >
            <Form>
              <FormikSelectField label="Country" name="country">
                {sortedRegions?.map(r => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </FormikSelectField>

              <FormikField
                name="zip"
                label="Zip"
                style={{
                  width: 110,
                }}
              />

              <Button color="turf-green" type="submit" className="w-full">
                Calculate Shipping
              </Button>
            </Form>
          </Formik>
        </Loader>
      </SimpleModal>
    </>
  );
}
